import { activeContactSignal, contactsSignal, selectContact } from '../state/contacts.store';
import { activeTabSignal } from '../state/ui.store';
import type { TabType } from '../types/common';

const TAB_SLUGS: Record<string, TabType> = {
  messages: 'tab-messages',
  actions: 'tab-actions',
  summaries: 'tab-summaries',
  projects: 'tab-summaries'
};

const TAB_NAMES: Record<TabType, string> = {
  'tab-messages': 'messages',
  'tab-actions': 'actions',
  'tab-summaries': 'summaries'
};

export interface RouteState {
  phone: string | null;
  tab: TabType;
}

/**
 * Detect if application is running in iOS Safari Standalone mode (added to Home Screen on iPhone/iPad).
 */
export function isIOSStandalone(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = ('standalone' in navigator && (navigator as any).standalone === true) ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  return isIOS && isStandalone;
}

/**
 * Parse current URL to extract phone and tab state.
 * Supports /c/:phone/:tab or query params ?c=:phone&tab=:tab
 */
export function parseUrlState(customPathname?: string, customSearch?: string): RouteState {
  const pathname = customPathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const searchParams = new URLSearchParams(customSearch ?? (typeof window !== 'undefined' ? window.location.search : ''));

  // Query parameter fallback (?c=5547...&tab=actions)
  const queryPhone = searchParams.get('c') || searchParams.get('phone');
  const queryTab = searchParams.get('tab');

  if (queryPhone) {
    const cleanPhone = queryPhone.replace(/\D/g, '');
    const mappedTab = queryTab && TAB_SLUGS[queryTab] ? TAB_SLUGS[queryTab] : 'tab-messages';
    return { phone: cleanPhone || queryPhone, tab: mappedTab };
  }

  // Pathname parsing: /c/:phone or /c/:phone/:tab
  const match = pathname.match(/^\/c\/([^\/]+)(?:\/([^\/]+))?/);
  if (match) {
    const rawPhone = match[1];
    const cleanPhone = rawPhone.replace(/\D/g, '');
    const tabSegment = match[2] ? match[2].toLowerCase() : 'messages';
    const mappedTab = TAB_SLUGS[tabSegment] || 'tab-messages';
    return { phone: cleanPhone || rawPhone, tab: mappedTab };
  }

  return { phone: null, tab: 'tab-messages' };
}

/**
 * Update current browser URL to reflect active contact and active tab without page reload.
 * Suppresses window.history.pushState/replaceState pathname changes when running in iOS Standalone mode
 * to prevent iOS WebKit from triggering Safari top header/controls.
 */
export function syncUrlWithState(options?: { replace?: boolean }) {
  if (typeof window === 'undefined') return;

  // In iOS Standalone mode (PWA on iPhone Home Screen), mutating pathname via pushState/replaceState
  // causes WebKit to show the Safari In-App Browser top bar and controls. We handle state in-memory only.
  if (isIOSStandalone()) {
    return;
  }

  const activeContact = activeContactSignal.value;
  const activeTab = activeTabSignal.value;

  if (!activeContact) {
    if (window.location.pathname !== '/' && window.location.pathname.startsWith('/c/')) {
      if (options?.replace) {
        window.history.replaceState(null, '', '/');
      } else {
        window.history.pushState(null, '', '/');
      }
    }
    return;
  }

  const phone = (activeContact.phone_number || activeContact.jid || '').replace(/\D/g, '') || activeContact.phone_number;
  const tabSlug = TAB_NAMES[activeTab] || 'messages';
  const newPath = `/c/${phone}/${tabSlug}`;

  if (window.location.pathname !== newPath) {
    if (options?.replace) {
      window.history.replaceState({ phone, tab: activeTab }, '', newPath);
    } else {
      window.history.pushState({ phone, tab: activeTab }, '', newPath);
    }
  }
}

/**
 * Initialize Router event listeners (Browser Back/Forward buttons).
 */
export function initRouter() {
  window.addEventListener('popstate', () => {
    const { phone, tab } = parseUrlState();
    if (!phone) {
      activeContactSignal.value = null;
      return;
    }

    const contacts = contactsSignal.value;
    const target = contacts.find(c => {
      const cClean = (c.phone_number || '').replace(/\D/g, '');
      return cClean === phone || c.phone_number === phone || c.jid === phone;
    });

    if (target) {
      if (!activeContactSignal.value || activeContactSignal.value.phone_number !== target.phone_number) {
        selectContact(target, { skipUrlSync: true });
      }
      activeTabSignal.value = tab;
    }
  });
}
